import React, { useMemo, useState } from "react";
import {
  AVERAGE_COMMISSION_PER_PRODUCT,
  calculateEarningsGoal,
} from "../../lib/earningsCalculator";
import { SectionHeading } from "../ui";
import { RevealGroup, RevealItem } from "../motion/Reveal";

const formatSek = (value) => `${Number(value || 0).toLocaleString("sv-SE")} kr`;

export function EarningsFactsSection({ compact = false, id = "kalkylator" }) {
  const [earningsTarget, setEarningsTarget] = useState(25000);
  const [sellerCount, setSellerCount] = useState(20);

  const metrics = useMemo(
    () => calculateEarningsGoal({ earningsTarget, sellerCount }),
    [earningsTarget, sellerCount],
  );
  const productLabel = metrics.productsPerSeller === 1 ? "produkt" : "produkter";

  return (
    <section id={id} className="scroll-mt-24 bg-slate-950 py-16 text-white md:py-20">
      <RevealGroup className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {!compact && (
          <RevealItem tone="text">
            <SectionHeading
              eyebrow="Räkna på gruppens mål"
              title="Se vad varje säljare behöver bidra med"
              description={`Dra i reglagen för att prova olika mål och gruppstorlekar. Kalkylen använder ${AVERAGE_COMMISSION_PER_PRODUCT} kr i genomsnittlig provision per såld produkt.`}
              align="center"
              className="[&_p]:text-white/80 [&_h2]:text-white [&_[data-eyebrow]]:text-amber-200"
            />
          </RevealItem>
        )}

        <RevealItem tone="text" className={`${compact ? "" : "mt-12"} grid items-stretch gap-8 lg:grid-cols-[1.05fr,0.95fr]`}>
          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6 md:p-8">
            <div className="mb-8 flex flex-col gap-2 rounded-2xl border border-amber-300/20 bg-amber-300/10 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-semibold text-amber-100">Genomsnittlig provision</span>
              <span className="text-xl font-bold text-amber-300">
                {AVERAGE_COMMISSION_PER_PRODUCT} kr{" "}
                <span className="text-sm font-semibold text-amber-100">per produkt</span>
              </span>
            </div>

            <div className="space-y-9">
              <div>
                <div className="mb-4 flex items-end justify-between gap-4">
                  <label htmlFor={`${id}-earnings-target`} className="text-sm font-semibold text-white/90">
                    Mål för gruppens förtjänst
                  </label>
                  <output
                    htmlFor={`${id}-earnings-target`}
                    className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-bold text-amber-300"
                  >
                    {formatSek(metrics.target)}
                  </output>
                </div>
                <input
                  id={`${id}-earnings-target`}
                  type="range"
                  min="5000"
                  max="100000"
                  step="1000"
                  value={earningsTarget}
                  onChange={(event) => setEarningsTarget(Number(event.target.value))}
                  className="h-2 w-full cursor-pointer accent-amber-400"
                />
                <div className="mt-3 flex justify-between text-xs text-white/50" aria-hidden="true">
                  <span>5 000 kr</span>
                  <span>100 000 kr</span>
                </div>
              </div>

              <div>
                <div className="mb-4 flex items-end justify-between gap-4">
                  <label htmlFor={`${id}-seller-count`} className="text-sm font-semibold text-white/90">
                    Antal säljare
                  </label>
                  <output
                    htmlFor={`${id}-seller-count`}
                    className="shrink-0 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-bold text-amber-300"
                  >
                    {metrics.sellers} st
                  </output>
                </div>
                <input
                  id={`${id}-seller-count`}
                  type="range"
                  min="1"
                  max="80"
                  step="1"
                  value={sellerCount}
                  onChange={(event) => setSellerCount(Number(event.target.value))}
                  className="h-2 w-full cursor-pointer accent-amber-400"
                />
                <div className="mt-3 flex justify-between text-xs text-white/50" aria-hidden="true">
                  <span>1 säljare</span>
                  <span>80 säljare</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-between rounded-3xl border border-amber-300/25 bg-gradient-to-br from-white/10 to-white/5 p-5 shadow-2xl shadow-slate-950/20 sm:p-6 md:p-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-amber-200">
                Ungefärligt mål per säljare
              </p>
              <div className="mt-7 rounded-2xl border border-white/10 bg-slate-900/60 px-5 py-8 text-center">
                <p className="text-5xl font-bold tracking-tight text-white md:text-6xl">
                  {metrics.productsPerSeller}
                </p>
                <p className="mt-3 text-lg font-semibold text-white/85">{productLabel} per säljare</p>
              </div>
            </div>

            <div className="mt-7 space-y-3">
              <p className="text-base leading-relaxed text-white/85">
                För att nå <strong className="text-white">{formatSek(metrics.target)}</strong> behöver var och en av gruppens{" "}
                <strong className="text-white">{metrics.sellers} säljare</strong> sälja cirka{" "}
                <strong className="text-amber-300">{metrics.productsPerSeller} {productLabel}</strong>.
              </p>
              <p className="text-sm leading-relaxed text-white/60">
                Det här är ett planeringsvärde. Den faktiska provisionen varierar mellan produkter och varianter.
              </p>
            </div>
          </div>
        </RevealItem>
      </RevealGroup>
    </section>
  );
}
